import {
  FanGetEventDocument,
  FanGetEventQuery,
  useFanGetEventQuery,
  useOnActiveEngagementChangedSubscription,
  useOnEngagementChangedSubscription,
  useOnMockEngagementChangedSubscription,
} from '@/generated/graphql';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';
import { useSearchParams } from 'react-router-dom';

type Options = Partial<
  Omit<Parameters<typeof useFanGetEventQuery>[0], 'variables'>
>;

export function useFanEvent(id: string, options: Options = {}) {
  const [searchParams] = useSearchParams();
  const engagementIdOverride = searchParams.get('engagementId');
  const { data, loading, error } = useFanGetEventQuery({
    ...options,
    variables: {
      id,
    },
  });

  // this will update the cache with the new active engagement
  useOnActiveEngagementChangedSubscription({
    variables: { eventId: id },
    onData: ({ client, data }) => {
      const nextActiveEngagement =
        data?.data?.activeEngagementChanged?.activeEngagement;

      // First, evict the existing activeEngagement from cache
      client.cache.evict({
        id: client.cache.identify({ __typename: 'Event', id }),
        fieldName: 'activeEngagement',
      });

      // Then update the cache with the new data
      client.cache.updateQuery<FanGetEventQuery>(
        {
          query: FanGetEventDocument,
          variables: { id },
        },
        (cachedData) => {
          if (!cachedData?.event) return null;

          return {
            event: {
              ...cachedData.event,
              activeEngagement: nextActiveEngagement
                ? {
                    ...nextActiveEngagement,
                    submissions: [], // Add missing required field
                  }
                : null,
            },
          };
        },
      );

      // Garbage collect any remaining deleted references
      client.cache.gc();
    },
  });

  const event = data?.event;
  const engagement = event?.activeEngagement;
  const mockRealtime = useMockRealtimeChannel();
  const skip =
    (!event || !engagement || !engagement.id) && !engagementIdOverride;

  useOnEngagementChangedSubscription({
    skip,
    variables: {
      engagementId: engagementIdOverride || engagement?.id || '',
    },
    onData: ({ data, client }) => {
      const changes = data?.data?.engagementChanged?.engagement;
      if (changes) {
        client.cache.updateQuery<FanGetEventQuery>(
          {
            query: FanGetEventDocument,
            variables: { id },
          },
          (cachedData) => {
            if (
              !cachedData?.event ||
              !cachedData.event.activeEngagement ||
              cachedData.event.activeEngagement.id !== changes.id
            ) {
              return cachedData;
            }

            return {
              event: {
                ...cachedData.event,
                activeEngagement: {
                  ...cachedData.event.activeEngagement!,
                  ...changes,
                },
              },
            } as FanGetEventQuery;
          },
        );
      }
    },
  });
  useOnMockEngagementChangedSubscription({
    skip: skip || !mockRealtime,
    variables: {
      engagementId: engagementIdOverride || engagement?.id || '',
    },
    onData: ({ data, client }) => {
      const changes = data?.data?.mockEngagementChanged?.engagement;
      if (changes) {
        client.cache.updateQuery<FanGetEventQuery>(
          {
            query: FanGetEventDocument,
            variables: { id },
          },
          (cachedData) => {
            if (
              !cachedData?.event ||
              !cachedData.event.activeEngagement ||
              cachedData.event.activeEngagement.id !== changes.id
            ) {
              return cachedData;
            }

            return {
              event: {
                ...cachedData.event,
                activeEngagement: {
                  ...cachedData.event.activeEngagement!,
                  ...changes,
                },
              },
            } as FanGetEventQuery;
          },
        );
      }
    },
  });

  return {
    data,
    loading,
    error,
  };
}
